import React, { useState } from 'react';
import { TagIcon, LightbulbIcon, PhoneIcon } from 'lucide-react';
import { PageHero } from '../components/PageHero';
import { SectionHeading } from '../components/SectionHeading';
import { CTABand } from '../components/CTABand';
import { site } from '../data/site';
import { tips } from '../data/tips';
type Category = 'Special Offers' | 'Tips';
const categories: {
  name: Category;
  icon: typeof TagIcon;
  blurb: string;
}[] = [{
  name: 'Special Offers',
  icon: TagIcon,
  blurb: 'Seasonal offers are posted here when they are running. Call the estimate line for anything currently available.'
}, {
  name: 'Tips',
  icon: LightbulbIcon,
  blurb: 'Practical planning and maintenance notes for Michigan homeowners, from kitchen prep to winter remodeling.'
}];
const archivePosts: {
  title: string;
  category: Category;
}[] = [{
  title: 'Gutter Cleaning Special',
  category: 'Special Offers'
}];
export function Specials() {
  const [active, setActive] = useState<Category>('Special Offers');
  const posts = archivePosts.filter(p => p.category === active);
  return <>
      <PageHero eyebrow="Specials & Tips" title="Offers and homeowner advice" description="This section holds our special offers and maintenance tips. Details for any current offer come from the estimate line." crumbs={[{
      label: 'Home',
      to: '/'
    }, {
      label: 'Specials & Tips'
    }]} data-id="element-512" data-mp="23cn05s0h7kuo" />

      <section className="mx-auto max-w-5xl px-6 py-12 md:py-16" data-id="element-513" data-mp="2hbcyfaz17qkm">
        <div className="grid gap-5 sm:grid-cols-2" data-id="element-514" data-mp="2m9o7hjhi5x1s">
          {categories.map(c => <button key={c.name} type="button" onClick={() => setActive(c.name)} aria-pressed={active === c.name} className={`rounded-sm border-2 bg-white p-6 text-left transition ${active === c.name ? 'border-safety shadow-card' : 'border-steel-200 hover:border-ink'}`} data-id="element-515" data-mp="2r168g7jizxmr">
              <c.icon className="h-6 w-6 text-safety" aria-hidden="true" data-id="element-516" data-mp="14zpwvfq0jac7" />
              <h2 className="mt-3 font-display text-2xl font-semibold uppercase tracking-tight text-ink" data-id="element-517" data-mp="100mphomk8s12">
                {c.name}
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-steel-500" data-id="element-518" data-mp="1equbuffouv71">
                {c.blurb}
              </p>
            </button>)}
        </div>

        <div className="mt-10" data-id="element-519" data-mp="1noe9leece9cw">
          <SectionHeading eyebrow="Archive" title={active} data-id="element-520" data-mp="1nriph9166tje" />
          {active === 'Tips' ? <div className="mt-5 grid gap-5 md:grid-cols-2" data-id="element-521" data-mp="1jpx4f0ehf8y4">
              {tips.map(tip => <article key={tip.heading} className="flex flex-col rounded-sm border border-steel-200 bg-white p-6 shadow-card" data-id="element-522" data-mp="1xlj1zvys7125">
                  <div className="hatch mb-4 h-1.5 w-12" aria-hidden="true" data-id="element-523" data-mp="3a4956ah4li6" />
                  <h3 className="font-display text-xl font-bold uppercase leading-tight tracking-tight text-ink" data-id="element-524" data-mp="3ugx966tj6el4">
                    {tip.heading}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-steel-500" data-id="element-525" data-mp="2ewo9v1dacrvy">
                    {tip.body}
                  </p>
                </article>)}
            </div> : posts.length === 0 ? <div className="mt-5 rounded-sm border border-dashed border-steel-300 bg-white p-10 text-center" data-id="element-526" data-mp="256332qngkh1y">
              <p className="font-display text-xl font-semibold uppercase tracking-tight text-ink" data-id="element-527" data-mp="1r6k8l0nkl484">
                Nothing posted in this section yet
              </p>
              <p className="mx-auto mt-2 max-w-md text-sm text-steel-500" data-id="element-528" data-mp="1h9p8idbnevrq">
                Call and ask — we are happy to talk through a project before you
                commit to it.
              </p>
            </div> : <ul className="mt-5 divide-y divide-steel-200 overflow-hidden rounded-sm border border-steel-200 bg-white" data-id="element-529" data-mp="3bnq8j1ixpote">
              {posts.map(p => <li key={p.title} className="flex flex-wrap items-center justify-between gap-3 px-5 py-5" data-id="element-530" data-mp="1gkk3kybzbsif">
                  <div data-id="element-531" data-mp="1chx4ccczb5b7">
                    <h3 className="font-display text-xl font-semibold uppercase tracking-tight text-ink" data-id="element-532" data-mp="1cjrrlxbq39ig">
                      {p.title}
                    </h3>
                    <p className="mt-1 text-xs font-bold uppercase tracking-[0.16em] text-steel-400" data-id="element-533" data-mp="12kizla0f44yq">
                      {p.category} · Archived post
                    </p>
                  </div>
                  <a href={site.estimatePhone.href} className="inline-flex items-center gap-2 rounded-sm border-2 border-ink px-4 py-2.5 text-sm font-bold uppercase tracking-wide text-ink transition hover:bg-ink hover:text-white" data-id="element-534" data-mp="stxv55y2c67w">
                    <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-535" data-mp="euf0nfy6cte2" />
                    Ask about it
                  </a>
                </li>)}
            </ul>}
          <p className="mt-4 text-sm text-steel-500" data-id="element-536" data-mp="3vx2r1f90muhm">
            Have a question about your own project? Call{' '}
            <a href={site.estimatePhone.href} className="font-semibold text-safety hover:underline" data-id="element-537" data-mp="4q8cqfpavtil">
              {site.estimatePhone.display}
            </a>
            .
          </p>
        </div>
      </section>

      <CTABand data-id="element-538" data-mp="eo5wor04n45e" />
    </>;
}